import API_ENDPOINTS from '../../../api/endpoints/apiEndpoints';
import {
  AdminSignupData,
  BuyerSignupData,
  IdentifierData,
  LoginData,
  LoginResponse,
  PasswordFindData,
  SellerSignupData,
  ValidateData,
} from './AuthInterfaces';
import { BaseResponse } from '../UtilsInterface';
import apiClient from '../../../api/apiClient';

const AuthService = {
  // 일반 로그인 요청
  login: async (loginBody: LoginData): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_login(),
        loginBody
      );

      const authHeader = response.headers['authorization'];
      if (authHeader) {
        const token = authHeader.split(' ')[1];
        localStorage.setItem('accessToken', token);
      }

      return response.data as LoginResponse;
    } catch (error) {
      console.error('로그인 실패:', error);
      throw error;
    }
  },

  // 로그아웃 요청
  logout: async (): Promise<BaseResponse<string>> => {
    try {
      const permissions = localStorage.getItem('permissions'); // permissions 추가

      let response;
      if (permissions === 'ADMIN') {
        response = await apiClient.post(API_ENDPOINTS.post_admin_logout());
      } else {
        response = await apiClient.post(API_ENDPOINTS.post_member_logout());
      }

      // 공통 데이터 제거
      localStorage.clear();
      sessionStorage.clear();

      // 쿠키 리셋
      document.cookie.split(';').forEach((cookie) => {
        const [name] = cookie.split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });

      return response.data;
    } catch (error) {
      console.error('로그아웃 실패:', error);
      throw error;
    }
  },

  // 구매자 회원가입 요청
  buyerSignup: async (
    buyerSignupBody: BuyerSignupData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_buyer_signup(),
        buyerSignupBody
      );
      return response.data;
    } catch (error) {
      console.error('구매자 회원가입 실패:', error);
      throw error;
    }
  },

  // 판매자 회원가입 요청
  sellerSignup: async (
    sellerSignupBody: SellerSignupData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_seller_signup(),
        sellerSignupBody
      );
      return response.data;
    } catch (error) {
      console.error('판매자 회원가입 실패:', error);
      throw error;
    }
  },

  // 아이디 중복 확인 요청
  identifierCheck: async (
    identifier: string
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_identifier_check(),
        { identifier }
      );
      return response.data;
    } catch (error) {
      console.error('아이디 중복 확인 실패:', error);
      throw error;
    }
  },

  // 사업자 등록번호 확인 요청
  sellerBusinessValidate: async (
    validataBody: ValidateData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_seller_business_validate(),
        validataBody
      );
      return response.data;
    } catch (error) {
      console.error('사업자 등록번호 확인 실패:', error);
      throw error;
    }
  },

  // 비밀번호 찾기 요청
  passwordFind: async (
    passwordFindBody: PasswordFindData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_password_find(),
        passwordFindBody
      );
      return response.data;
    } catch (error) {
      console.error('비밀번호 찾기 실패:', error);
      throw error;
    }
  },

  // 아이디 이메일 전송 요청
  identifierSend: async (
    identifierSendBody: IdentifierData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_identifier_send(),
        identifierSendBody
      );
      return response.data;
    } catch (error) {
      console.error('아이디 이메일 전송 실패:', error);
      throw error;
    }
  },

  // 아이디 찾기 요청
  identifierFind: async (
    identifierFind: IdentifierData
  ): Promise<BaseResponse<{ identifier: string; regDate: string }>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_identifier_find(),
        identifierFind
      );
      return response.data;
    } catch (error) {
      console.error('아이디 찾기 실패:', error);
      throw error;
    }
  },

  // 관리자 회원가입 요청
  adminSignup: async (
    adminSignupBody: AdminSignupData
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_admin_signup(),
        adminSignupBody
      );
      return response.data;
    } catch (error) {
      console.error('관리자 회원가입 실패:', error);
      throw error;
    }
  },

  // 이메일 인증 코드 전송 요청
  sendEmailVerificationCode: async (
    email: string
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_email_verification_code_send(),
        { email }
      );
      return response.data;
    } catch (error) {
      console.error('이메일 인증 코드 전송 실패:', error);
      throw error;
    }
  },

  // 이메일 인증 코드 확인 요청
  validateEmailVerificationCode: async (
    email: string,
    code: string
  ): Promise<BaseResponse<string>> => {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.post_email_verification_code_validate(),
        { email, code }
      );
      return response.data;
    } catch (error) {
      console.error('이메일 인증 코드 확인 실패:', error);
      throw error;
    }
  },

  // 은행 목록 조회 요청
  getSellerBusinessBanks: async (): Promise<BaseResponse<string[]>> => {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.get_seller_business_banks()
      );
      return response.data;
    } catch (error) {
      console.error('은행 목록 조회 실패:', error);
      throw error;
    }
  },
};

export default AuthService;
